import { animated, useSpring } from "@react-spring/web";
import classNames from "classnames/bind";
import React, {
  cloneElement,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import { Portal } from "../Portal";
import styles from "./Menu.module.scss";
import type { MenuProps } from "./types";

const cx = classNames.bind(styles);
const ANIMATION_DURATION = 150;

// IMPROVE: this component needs a comprehensive refactor of props and internal
// logic. There a plethora of code smells and React anti-patterns here.
/**
 * A wrapper component that allows adding a standardized menu to an arbitrary component
 */
const Menu = ({
  isFluid,
  children,
  content,
  isOpen: isOpenProp,
  onClose,
  onOpen,
  onChange,
  target,
  onScroll,
  align = "left",
}: MenuProps) => {
  const [isOpen, setIsOpen] = useState<boolean>(isOpenProp ?? false);
  const [isMounted, setIsMounted] = useState<boolean>();
  const [triggerRef, setTriggerRef] = useState<HTMLElement>();
  const [triggerBoundingRect, setTriggerBoundingRect] = useState<DOMRect>();
  const [contentRef, setContentRef] = useState<HTMLElement>();
  const [contentHeight, setContentHeight] = useState<number | undefined>();

  const clientResizeObserver = React.useRef<ResizeObserver>(null);

  if (clientResizeObserver.current === null) {
    // @ts-expect-error: Generally, rewriting current in this fashion is not allowed, but there are specific cases where it is proper (https://beta.reactjs.org/apis/react/useRef#avoiding-recreating-the-ref-contents)
    clientResizeObserver.current = new ResizeObserver((entries) => {
      if (entries?.[0]) {
        setTriggerBoundingRect(
          (entries[0].target as HTMLElement).getBoundingClientRect()
        );
      }
    });
  }

  const contentResizeObserver = React.useRef<ResizeObserver>(null);

  if (contentResizeObserver.current === null) {
    // @ts-expect-error: Generally, rewriting current in this fashion is not allowed, but there are specific cases where it is proper (https://beta.reactjs.org/apis/react/useRef#avoiding-recreating-the-ref-contents)
    contentResizeObserver.current = new ResizeObserver((entries) => {
      if (entries?.[0]) {
        setContentHeight(
          (entries[0].target as HTMLElement).getBoundingClientRect().height
        );
      }
    });
  }

  const recalculateTriggerBounds = () => {
    if (!triggerRef) return;

    setTriggerBoundingRect(triggerRef.getBoundingClientRect());
  };

  const handleClose = () => {
    setIsOpen(false);
    onClose?.();
    onChange?.(false);
    recalculateTriggerBounds();
  };

  const handleOpen = () => {
    setIsOpen(true);
    onOpen?.();
    onChange?.(true);
    recalculateTriggerBounds();
  };

  const getLeftPosition = () =>
    target ? (triggerRef?.offsetLeft ?? 0) : (triggerBoundingRect?.left ?? 0);

  const getRightPosition = () =>
    getLeftPosition() + (triggerBoundingRect?.width ?? 0);

  const handleClientRef = useCallback((node) => {
    if (node && clientResizeObserver?.current) {
      setTriggerRef(node);
      clientResizeObserver.current.observe(node);
    }
  }, []);

  const handleContentRef = useCallback((node) => {
    if (node && contentResizeObserver?.current) {
      setContentRef(node);
      contentResizeObserver.current.observe(node);
    }
  }, []);

  const [animationStyle, api] = useSpring(() => ({
    config: { duration: ANIMATION_DURATION },
    height: 0,
    opacity: 0.5,
  }));

  useEffect(() => {
    if (isMounted) {
      return;
    }

    if (isOpenProp ?? isOpen) {
      setIsMounted(true);
    } else {
      setTimeout(() => {
        setIsMounted(false);
      }, ANIMATION_DURATION);
    }
  }, [isOpenProp, isOpen]);

  useEffect(() => {
    if (isOpenProp ?? isOpen) {
      api.start({ opacity: 1, height: contentHeight });
    } else {
      api.start({ height: 0, opacity: 0.5 });
    }
  }, [isOpen, isOpenProp, contentHeight]);

  useEffect(() => {
    const handleResize = () => handleClose();

    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, [triggerRef]);

  useEffect(() => {
    const onClickOutside = (event: MouseEvent) => {
      // when the content or the trigger ref is unset, the menu is closed, the
      // clicked target is within the menu content, or the clicked target is
      // within the trigger content, do nothing
      if (
        !contentRef ||
        !triggerRef ||
        !(isOpenProp ?? isOpen) ||
        contentRef.contains(event.target as Node) ||
        triggerRef.contains(event.target as Node)
      ) {
        return;
      }

      handleClose();
    };

    document.addEventListener("click", onClickOutside);

    return () => {
      document.removeEventListener("click", onClickOutside);
    };
  }, [contentRef, isOpen, isOpenProp]);

  useEffect(() => {
    const handleScroll = () => recalculateTriggerBounds();

    window.addEventListener("scroll", handleScroll, true);

    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, [triggerRef]);

  const style = useMemo(() => {
    const width = isFluid ? triggerBoundingRect?.width : undefined;

    let horizontalPosition = 0;

    const contentWidth = contentRef?.getBoundingClientRect().width ?? 0;

    if (align === "left") {
      const leftPosition = getLeftPosition();

      if (leftPosition + contentWidth > window.innerWidth) {
        horizontalPosition = getRightPosition() - contentWidth;
      } else {
        horizontalPosition = leftPosition;
      }
    } else {
      const rightPosition = getRightPosition() - contentWidth;

      if (rightPosition < 0) {
        horizontalPosition = getLeftPosition();
      } else {
        horizontalPosition = rightPosition;
      }
    }

    const topValue =
      (target ? triggerRef?.offsetTop : triggerBoundingRect?.top) ?? 0;

    // We only want to check for overflow if the menu is portalled to the window. Otherwise, we want to let the menu overflow the parent container.
    const isOverflowing = target
      ? false
      : (topValue ?? 0) +
          (triggerBoundingRect?.height ?? 0) +
          (contentHeight ?? 0) >
        window.innerHeight;

    const top = isOverflowing
      ? window.innerHeight - (contentHeight ?? 0)
      : topValue + (triggerBoundingRect?.height ?? 0);

    return {
      ...animationStyle,
      width,
      left: horizontalPosition,
      top,
    };
  }, [triggerBoundingRect, animationStyle, isFluid, contentRef, triggerRef]);

  return (
    <>
      {cloneElement(children, {
        ref: handleClientRef,
        onClick: (event: React.MouseEvent) => {
          handleOpen();
          children?.props?.onClick?.(event);
        },
      })}

      {isMounted ? (
        <Portal target={target}>
          <animated.div
            className={cx("container")}
            style={style}
            role="menu"
          >
            <div
              ref={handleContentRef}
              onScroll={onScroll}
            >
              {content}
            </div>
          </animated.div>
        </Portal>
      ) : null}
    </>
  );
};

export default Menu;
